import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Phone, MapPin, Eye, CheckCircle, XCircle, Filter } from 'lucide-react';
import { collection, getDocs, updateDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';

const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // جلب جميع أنواع الحجوزات (خدمات + استشارات تعديلات)
      const [bookingOrders, modificationOrders] = await Promise.all([
        getDocs(query(collection(db, COLLECTIONS.ORDERS), where('type', '==', 'booking'))),
        getDocs(query(collection(db, COLLECTIONS.ORDERS), where('type', '==', 'modification-consultation')))
      ]);

      const bookingsData = [];
      bookingOrders.forEach((doc) => {
        bookingsData.push({ id: doc.id, ...doc.data() });
      });
      modificationOrders.forEach((doc) => {
        bookingsData.push({ id: doc.id, ...doc.data() });
      });

      setBookings(bookingsData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.ORDERS, bookingId), { status: newStatus });
      loadData();
    } catch (error) {
      console.error('Error updating booking:', error);
      if (error.code === 'not-found') {
        alert('هذا الحجز غير موجود في قاعدة البيانات. سيتم إزالته من القائمة.');
        setBookings(prev => prev.filter(b => b.id !== bookingId));
      } else {
        alert(`حدث خطأ أثناء تحديث الحجز: ${error.message}`);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'in-progress': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-purple-100 text-purple-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'مؤكد';
      case 'pending': return 'قيد الانتظار';
      case 'in-progress': return 'جاري التنفيذ';
      case 'completed': return 'مكتمل';
      case 'cancelled': return 'ملغي';
      default: return status;
    }
  };

  const filteredBookings = filterStatus === 'all'
    ? bookings
    : bookings.filter(b => b.status === filterStatus);

  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    inProgress: bookings.filter(b => b.status === 'in-progress').length,
    completed: bookings.filter(b => b.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">إدارة الحجوزات</h1>
          <p className="text-gray-600 mt-2">إجمالي الحجوزات: {bookings.length}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 border-r-4 border-gray-500">
          <p className="text-sm text-gray-600">الإجمالي</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-yellow-50 rounded-xl shadow-md p-4 border-r-4 border-yellow-500">
          <p className="text-sm text-yellow-700">قيد الانتظار</p>
          <p className="text-2xl font-bold text-yellow-800">{stats.pending}</p>
        </div>
        <div className="bg-green-50 rounded-xl shadow-md p-4 border-r-4 border-green-500">
          <p className="text-sm text-green-700">مؤكدة</p>
          <p className="text-2xl font-bold text-green-800">{stats.confirmed}</p>
        </div>
        <div className="bg-blue-50 rounded-xl shadow-md p-4 border-r-4 border-blue-500">
          <p className="text-sm text-blue-700">جاري التنفيذ</p>
          <p className="text-2xl font-bold text-blue-800">{stats.inProgress}</p>
        </div>
        <div className="bg-purple-50 rounded-xl shadow-md p-4 border-r-4 border-purple-500">
          <p className="text-sm text-purple-700">مكتملة</p>
          <p className="text-2xl font-bold text-purple-800">{stats.completed}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <span className="font-medium text-gray-700">تصفية:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'الكل', color: 'gray' },
            { value: 'pending', label: 'قيد الانتظار', color: 'yellow' },
            { value: 'confirmed', label: 'مؤكدة', color: 'green' },
            { value: 'in-progress', label: 'جاري التنفيذ', color: 'blue' },
            { value: 'completed', label: 'مكتملة', color: 'purple' },
            { value: 'cancelled', label: 'ملغية', color: 'red' }
          ].map((filter) => (
            <button
              key={filter.value}
              onClick={() => setFilterStatus(filter.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterStatus === filter.value
                ? `bg-${filter.color}-500 text-white`
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {filteredBookings.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span className="font-bold">حجز #{booking.id?.slice(0, 8)}</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                    {getStatusText(booking.status)}
                  </span>
                </div>
              </div>

              <div className="p-6 space-y-3">
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {booking.type === 'modification-consultation' ? 'التعديل' : 'الخدمة'}
                  </p>
                  <p className="font-bold text-lg text-gray-900">
                    {booking.serviceName || booking.modificationName || 'غير محدد'}
                  </p>
                  {booking.type === 'modification-consultation' && (
                    <span className="inline-block mt-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                      استشارة تعديل
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <User className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm">{String(booking.customerName || '')}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm">{String(booking.customerPhone || '')}</span>
                </div>

                {booking.customerAddress && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <MapPin className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm">{String(booking.customerAddress)}</span>
                  </div>
                )}

                {booking.bookingDate && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm">{String(booking.bookingDate)} - {String(booking.bookingTime)}</span>
                  </div>
                )}

                {(booking.carModel || booking.carType || booking.carYear) && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-blue-700 font-medium mb-1">معلومات السيارة:</p>
                    <p className="text-sm text-blue-900">
                      {booking.carType && `${booking.carType} `}
                      {booking.carModel && `${booking.carModel} `}
                      {booking.carYear && `(${booking.carYear})`}
                    </p>
                  </div>
                )}

                <div className="pt-4 border-t space-y-2">
                  <select
                    value={booking.status}
                    onChange={(e) => handleStatusChange(booking.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                  >
                    <option value="pending">قيد الانتظار</option>
                    <option value="confirmed">مؤكد</option>
                    <option value="in-progress">جاري التنفيذ</option>
                    <option value="completed">مكتمل</option>
                    <option value="cancelled">ملغي</option>
                  </select>

                  <button
                    onClick={() => { setSelectedBooking(booking); setShowModal(true); }}
                    className="w-full bg-gray-800 hover:bg-gray-900 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    عرض التفاصيل
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>لا توجد حجوزات</p>
        </div>
      )}

      {showModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-8">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">تفاصيل الحجز</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">رقم الحجز</p>
                  <p className="font-medium">#{selectedBooking.id?.slice(0, 12)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {selectedBooking.type === 'modification-consultation' ? 'التعديل' : 'الخدمة'}
                  </p>
                  <p className="font-medium">
                    {String(selectedBooking.serviceName || selectedBooking.modificationName || '')}
                  </p>
                  {selectedBooking.type === 'modification-consultation' && (
                    <span className="inline-block mt-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                      استشارة تعديل
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">اسم العميل</p>
                  <p className="font-medium">{String(selectedBooking.customerName || '')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">رقم الهاتف</p>
                  <p className="font-medium">{String(selectedBooking.customerPhone || '')}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">البريد الإلكتروني</p>
                  <p className="font-medium">{String(selectedBooking.customerEmail || 'غير متوفر')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">العنوان</p>
                  <p className="font-medium">{String(selectedBooking.customerAddress || '')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">تاريخ الحجز</p>
                  <p className="font-medium">{String(selectedBooking.bookingDate || 'غير محدد')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">الوقت</p>
                  <p className="font-medium">{String(selectedBooking.bookingTime || 'غير محدد')}</p>
                </div>
              </div>
            </div>

            {(selectedBooking.carModel || selectedBooking.carType || selectedBooking.carYear) && (
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-600 mb-2">معلومات السيارة</p>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4">
                    {selectedBooking.carType && (
                      <div>
                        <p className="text-xs text-blue-700">النوع</p>
                        <p className="font-medium text-blue-900">{String(selectedBooking.carType)}</p>
                      </div>
                    )}
                    {selectedBooking.carModel && (
                      <div>
                        <p className="text-xs text-blue-700">الموديل</p>
                        <p className="font-medium text-blue-900">{String(selectedBooking.carModel)}</p>
                      </div>
                    )}
                    {selectedBooking.carYear && (
                      <div>
                        <p className="text-xs text-blue-700">سنة الصنع</p>
                        <p className="font-medium text-blue-900">{String(selectedBooking.carYear)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {selectedBooking.notes && (
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-600 mb-2">ملاحظات</p>
                <p className="bg-gray-50 rounded-lg p-4">{String(selectedBooking.notes)}</p>
              </div>
            )}

            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">الحالة</span>
                <span className={`px-4 py-2 rounded-full font-medium ${getStatusColor(selectedBooking.status)}`}>
                  {getStatusText(selectedBooking.status)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsManagement;
